import type {Root as HtmlRoot} from 'hast'
import {raw as parseRawNodes} from 'hast-util-raw'
import type {Root as MarkdownRoot} from 'mdast'
import {toHast} from 'mdast-util-to-hast'
import {EXIT, visit} from 'unist-util-visit'
import {VFile} from 'vfile'

import {hastDelimiters} from './extensions/delimiters'
import {hastDistinguishAdjacentElements} from './extensions/distinguish-adjacent-elements'
import {hastExpandElementsToEnd} from './extensions/expand-elements-to-end'
import {highlightCode} from './extensions/highlight-code'
import {convertVideoLinks} from './extensions/video-links'
import {sanitizeHtml} from './sanitize-html'

/** Check if the Markdown tree has any raw `html` nodes. */
function hasHtmlNodes(tree: MarkdownRoot) {
  let hasHtml = false
  visit(tree, 'html', () => {
    hasHtml = true
    return EXIT
  })
  return hasHtml
}

/**
 * Convert the Markdown (mdast) AST to an HTML (hast) AST, passing `delimiter` nodes through unchanged.
 */
export function buildHtmlTree(markdownTree: MarkdownRoot, markdownSource: string, parseRaw = true): HtmlRoot {
  const fromMarkdown = toHast(markdownTree, {
    ...hastDelimiters,
    allowDangerousHtml: true,
  }) as HtmlRoot

  hastDistinguishAdjacentElements(fromMarkdown)
  highlightCode(fromMarkdown, markdownSource)

  // If the tree has no HTML, we can save a lot of time by just returning the tree as-is.
  // Since it was created from the Markdown tree, we know we can trust it and skip sanitization.
  if (!parseRaw || !hasHtmlNodes(markdownTree)) {
    convertVideoLinks(fromMarkdown)
    return fromMarkdown
  }

  const dangerousReparsedHtml = parseRawNodes(fromMarkdown, {
    // tagFilter will filter out certain very unsafe tags. Even though we will sanitize, it doesn't hurt to enable this
    tagfilter: true,
    passThrough: ['delimiter'],
    // A virtual file is required for position data to be maintained
    file: new VFile(markdownSource),
  }) as HtmlRoot

  hastExpandElementsToEnd(dangerousReparsedHtml, markdownSource)

  // The reparsed tree is dangerous and MUST be sanitized before rendering
  const sanitized = sanitizeHtml(dangerousReparsedHtml)
  convertVideoLinks(sanitized)
  return sanitized
}
